/**
 * Skill Matrix Hooks
 *
 * TanStack Query hooks for skill matrix operations including:
 * - Aggregated operator × SOP matrix (flat or grouped)
 * - Cell drill-down for history
 * - CSV export
 * - Group comparison (Phase 9.3)
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  SkillMatrixResponse,
  SkillMatrixQuery,
  SkillMatrixGroupedResponse,
  SkillMatrixGroupCellResponse,
} from '@guild/shared';

// Query keys
export const skillMatrixKeys = {
  all: ['skill-matrix'] as const,
  matrix: (filters?: SkillMatrixQuery) => [...skillMatrixKeys.all, 'matrix', filters] as const,
  groupCell: (groupBy: string, groupId: string, sopId: string, departmentId?: string) =>
    [...skillMatrixKeys.all, 'group-cell', groupBy, groupId, sopId, departmentId ?? ''] as const,
  cell: (operatorId: string, sopId: string) =>
    [...skillMatrixKeys.all, 'cell', operatorId, sopId] as const,
};

// Cell drill-down response type
interface CellDrillDownResponse {
  operator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  sop: {
    id: string;
    title: string;
  };
  stats: {
    assessmentCount: number;
    averageScore: number;
    highestScore: number;
    lowestScore: number;
    firstAssessment: string;
    lastAssessment: string;
  } | null;
  history: Array<{
    id: string;
    assessmentType: 'auto' | 'manual';
    complianceScore: number;
    efficiencyScore: number;
    safetyScore: number;
    overallScore: number;
    assessedAt: string;
    managerNotes: string | null;
  }>;
}

// ============================================
// QUERIES
// ============================================

/**
 * Fetch skill matrix data (flat or grouped)
 *
 * Phase 9.3: Supports groupBy parameter for shift/position/station aggregation.
 * Returns SkillMatrixGroupedResponse when groupBy is set (not 'operator').
 */
export function useSkillMatrix(filters?: SkillMatrixQuery) {
  const api = useApi();

  return useQuery({
    queryKey: skillMatrixKeys.matrix(filters),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters?.departmentId) params.set('departmentId', filters.departmentId);
      if (filters?.operatorIds?.length) {
        filters.operatorIds.forEach((id) => params.append('operatorIds', id));
      }
      if (filters?.sopIds?.length) {
        filters.sopIds.forEach((id) => params.append('sopIds', id));
      }
      // Phase 9.3: GroupBy support
      if (filters?.groupBy) params.set('groupBy', filters.groupBy);
      if (filters?.shiftId) params.set('shiftId', filters.shiftId);
      if (filters?.positionId) params.set('positionId', filters.positionId);
      if (filters?.stationId) params.set('stationId', filters.stationId);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/skill-matrix?${queryString}` : '/api/skill-matrix';

      // Return type varies based on groupBy
      return api.get<SkillMatrixResponse | SkillMatrixGroupedResponse>(endpoint);
    },
  });
}

/**
 * Fetch drill-down data for a specific cell
 */
export function useSkillMatrixCell(operatorId: string | null, sopId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: skillMatrixKeys.cell(operatorId ?? '', sopId ?? ''),
    queryFn: () =>
      api.get<CellDrillDownResponse>(
        `/api/skill-matrix/cell?operatorId=${operatorId}&sopId=${sopId}`
      ),
    enabled: !!operatorId && !!sopId,
  });
}

/**
 * Fetch group cell drill-down: per-operator breakdown within a group for one SOP
 */
export function useSkillMatrixGroupCell(
  groupBy: string | null,
  groupId: string | null,
  sopId: string | null,
  departmentId?: string | null
) {
  const api = useApi();

  return useQuery({
    queryKey: skillMatrixKeys.groupCell(groupBy ?? '', groupId ?? '', sopId ?? '', departmentId ?? undefined),
    queryFn: () => {
      const params = new URLSearchParams({
        groupBy: groupBy!,
        groupId: groupId!,
        sopId: sopId!,
      });
      if (departmentId) params.set('departmentId', departmentId);
      return api.get<SkillMatrixGroupCellResponse>(
        `/api/skill-matrix/group-cell?${params}`
      );
    },
    enabled: !!groupBy && !!groupId && !!sopId,
  });
}

// ============================================
// EXPORT
// ============================================

/**
 * Export skill matrix as CSV
 *
 * Phase 9.3: Supports groupBy parameter for grouped export.
 */
export function useExportSkillMatrix() {
  const api = useApi();

  return useMutation({
    mutationFn: async (filters?: SkillMatrixQuery) => {
      const params = new URLSearchParams();

      if (filters?.departmentId) params.set('departmentId', filters.departmentId);
      // Phase 9.3: GroupBy support for export
      if (filters?.groupBy) params.set('groupBy', filters.groupBy);
      if (filters?.shiftId) params.set('shiftId', filters.shiftId);
      if (filters?.positionId) params.set('positionId', filters.positionId);
      if (filters?.stationId) params.set('stationId', filters.stationId);

      const queryString = params.toString();
      const endpoint = queryString
        ? `/api/skill-matrix/export?${queryString}`
        : '/api/skill-matrix/export';

      const response = await api.getBlob(endpoint);
      return response;
    },
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `skill-matrix-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ============================================
// PHASE 9.3: GROUP COMPARISON
// ============================================

// Query keys for group comparison
export const groupComparisonKeys = {
  all: ['group-comparison'] as const,
  comparison: (groupBy: string, sopId?: string) =>
    [...groupComparisonKeys.all, groupBy, sopId] as const,
};

// Group comparison response type
interface GroupComparisonResponse {
  groupBy: 'shift' | 'position' | 'station';
  period: number;
  comparison: Array<{
    groupId: string;
    groupName: string;
    groupCode: string | null;
    rank: number;
    avgScore: number | null;
    avgCompliance: number | null;
    avgEfficiency: number | null;
    avgSafety: number | null;
    assessmentCount: number;
    operatorCount: number;
  }>;
  summary: {
    totalGroups: number;
    groupsWithData: number;
    totalAssessments: number;
    totalOperators: number;
    overallAverage: number | null;
  };
}

/**
 * Fetch group comparison data (Phase 9.3)
 *
 * Compare performance across shifts, positions, or stations.
 */
export function useGroupComparison(
  groupBy: 'shift' | 'position' | 'station',
  options?: { sopId?: string; period?: number; departmentId?: string }
) {
  const api = useApi();

  return useQuery({
    queryKey: groupComparisonKeys.comparison(groupBy, options?.sopId),
    queryFn: async () => {
      const params = new URLSearchParams({ groupBy });
      if (options?.sopId) params.set('sopId', options.sopId);
      if (options?.period) params.set('period', String(options.period));
      if (options?.departmentId) params.set('departmentId', options.departmentId);

      return api.get<GroupComparisonResponse>(`/api/analytics/group-comparison?${params}`);
    },
    enabled: !!groupBy,
  });
}
